import { useEffect, useState } from "react";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { useLocation } from "react-router-dom";

import useStore from "../../store";
import HamburgerMenu from "./HamburgerMenu";
import useTranslation from "../../hooks/useTranslation";
import LanguageDropdown from "./LanguageDropdown";

import PaymentModal from "./PaymentModal";
import PaymentTypeModal from "./components/PaymentTypeModal";
import WaiterSuccessModal from "./components/WaiterSuccessModal";

import { isApiSuccess, navigateSameTab } from "./paymentHelpers";
import "./header.scss";

const Header = () => {
  const { company, showInvoice, setShowInvoice } = useStore((state) => state);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tableId = queryParams.get("table_id");

  const [order, setOrder] = useState([]);
  const { tr } = useTranslation();

  // Waiter call states
  const [waiterLoading, setWaiterLoading] = useState(false);
  const [showWaiterSuccess, setShowWaiterSuccess] = useState(false);

  const path = location.pathname.split("/").pop();

  const orderSum = (order ?? []).reduce(
    (sum, it) => sum + (Number(it?.price) || 0),
    0
  );
  const paidSum = (order ?? []).reduce(
    (sum, it) => sum + (Number(it?.paid_amount) || 0),
    0
  );

  const callWaiter = async () => {
    if (!tableId) {
      enqueueSnackbar(tr("Стол не выбран"), { variant: "warning" });
      return;
    }

    setWaiterLoading(true);
    try {
      await axios({
        url: `${process.env.REACT_APP_API_URL}online-menu/tables/${tableId}/waiter/call`,
        data: { table_id: tableId, organization_id: path },
        method: "POST",
      });
      setWaiterLoading(false);
      setShowWaiterSuccess(true);
    } catch (error) {
      setWaiterLoading(false);
      enqueueSnackbar(
        error?.response?.data?.message || tr("Не удалось вызвать официанта"),
        { variant: "error" }
      );
    }
  };

  // --- Payment oqimi ---
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  const [showPayModal, setShowPayModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const onPayClickOpenModal = () => {
    setShowTypeModal(true); // avval payment_types tanlanadi
  };

  const handleSelectPaymentType = (opt) => {
    setSelectedPaymentType(opt);
    setShowTypeModal(false);
    setShowPayModal(true); // keyin mavjud PaymentModal ochiladi
  };

  const handleModalSubmit = async (amountNum) => {
    setPayLoading(true);
    try {
      await paymentBtn(amountNum, selectedPaymentType);
      setShowPayModal(false);
    } finally {
      setPayLoading(false);
    }
  };

  const paymentBtn = async (amountParam, selectedType) => {
    try {
      if (!company?.id) {
        enqueueSnackbar(tr("Организация не найдена"), { variant: "warning" });
        return;
      }

      const orders = Array.isArray(order) ? order : [];
      const orderIds = orders.map((o) => o?.id).filter(Boolean);

      if (orderIds.length === 0) {
        enqueueSnackbar(tr("Нет активных заказов"), { variant: "warning" });
        return;
      }

      const payment_type =
        selectedType?.alias ||
        selectedType?.key ||
        company?.payment_types?.[0]?.name ||
        "hamkorbank";

      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}app/orders/remote/pay`,
        {
          payment_type,
          order_ids: orderIds,
          amount: amountParam ?? 0,
        }
      );

      const ok = isApiSuccess(resp);
      const link = resp?.data?.data?.result?.url;

      if (ok && link) {
        navigateSameTab(link);
        enqueueSnackbar(tr("Ссылка на оплату отправлена"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(tr("Не удалось получить ссылку на оплату"), {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || tr("Не удалось провести оплату"),
        { variant: "error" }
      );
    }
  };

  useEffect(() => {
    if (!tableId) return;

    axios({
      url: `${process.env.REACT_APP_API_URL}online-menu/tables/${tableId}/orders/all`,
      params: { include: "activeOrders" },
      method: "GET",
    })
      .then((response) => {
        const data = response?.data;
        setOrder(Array.isArray(data) ? data : data?.orders || []);
      })
      .catch(() => {
        setOrder([]);
      });
  }, [tableId, showInvoice]);

  return (
    <>
      {path === "qr-code" ? (
        <div />
      ) : (
        <div className="navbar-header">
          <div className="navbar-header__background">
            <div className="container">
              {/* Top row: Hamburger menu left, Language dropdown right */}
              <div className="navbar-header__top">
                <HamburgerMenu />
                <LanguageDropdown />
              </div>

              {/* Middle section: Logo, Company name, Call Waiter button */}
              <div className="navbar-header__middle">
                {company?.logo?.thumbnails?.normal?.src && (
                  <div className="navbar-header__logo">
                    <img
                      src={company?.logo?.thumbnails?.normal?.src}
                      alt="logo"
                    />
                  </div>
                )}

                <h2 className="navbar-header__company-name">
                  {company?.name}
                </h2>

                {tableId && (
                    <button
                        onClick={callWaiter}
                        className="navbar-header__call-waiter-btn"
                        disabled={waiterLoading}
                    >
                      {waiterLoading ? (
                          <>
                            <img
                                className="navbar-header__loading-spinner"
                                src={process.env.PUBLIC_URL + '/loading.svg'}
                                alt="loading"
                            />
                            <span>{tr("Отправка...")}</span>
                          </>
                      ) : (
                          <>
                            <i className="fas fa-bell"></i>
                            {tr("Подозвать официанта")}
                          </>
                      )}
                    </button>
                )}
              </div>
            </div>
          </div>

          {/* Additional buttons row (for invoice and payment) */}
          {Array.isArray(order) && order.length > 0 && (
            <div className="navbar-header__actions">
              <div className="container">
                <div className="navbar-header__action-buttons">
                  <button
                    onClick={() => setShowInvoice(true)}
                    className="navbar-header__action-btn"
                  >
                    <i className="fas fa-file-invoice"></i>
                    {tr("Просмотреть счет")}
                  </button>

                  {company?.payment_systems?.length > 0 && (
                    <button
                      onClick={onPayClickOpenModal}
                      className="navbar-header__action-btn navbar-header__action-btn--payment"
                      title={
                        orderSum
                          ? `${tr("Сумма по заказам")}: ${orderSum}`
                          : undefined
                      }
                    >
                      <i className="fas fa-credit-card"></i>
                      {tr("Оплата")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 1-qadam: payment_types tanlash */}
      <PaymentTypeModal
        open={showTypeModal}
        options={company?.payment_systems || []}
        onClose={() => setShowTypeModal(false)}
        onSelect={handleSelectPaymentType}
        tr={tr}
      />

      {/* 2-qadam: mavjud PaymentModal */}
      <PaymentModal
        open={showPayModal}
        orderSum={orderSum}
        paidSum={paidSum}
        loading={payLoading}
        onClose={() => setShowPayModal(false)}
        onSubmit={handleModalSubmit}
        tr={tr}
      />

      {/* Waiter Success Modal */}
      <WaiterSuccessModal
        open={showWaiterSuccess}
        onClose={() => setShowWaiterSuccess(false)}
        tr={tr}
      />
    </>
  );
};

export default Header;
